import React from "react";

import { Close as CloseIcon, Image as ImageIcon } from "@mui/icons-material";
import {
  Box,
  Checkbox as MUICheckbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Theme,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles, withStyles } from "tss-react/mui";

import { FormElementKind } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { humanize } from "@/utils/string";

import {
  renderDefaultTipTapHTML,
  useTipTapEditor,
} from "./FormBuilder/components/utils/helpers";
import {
  StyledFileIcon,
  StyledImageIcon,
} from "./FormBuilder/components/utils/Icons";
import Form from "./FormBuilder/store/Form";
import FormElement from "./FormBuilder/store/FormElement";

type Props = {
  form: Form;
  onClose: () => void;
  open: boolean;
};

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    padding: theme.spacing(8),
  },
  modal: {
    padding: theme.spacing(4),
    width: "100%",
    height: "90vh",
    backgroundColor: colors.white,
    overflowY: "auto",
  },
  sectionContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(8),
  },
  section: {
    padding: theme.spacing(4),
    _backgroundColor: colors.white,
    get backgroundColor() {
      return this._backgroundColor;
    },
    set backgroundColor(value) {
      this._backgroundColor = value;
    },
  },
  sectionTitle: {
    borderRadius: "8px 8px 0px 0px",
    background: "#75A8C6",
    color: colors.white,
    padding: theme.spacing(3, 4),
  },
  sectionActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  border: {
    borderBottom: "solid 4px #F3F4F6",
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    fontWeight: 600,
  },
  description: {
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    fontWeight: 600,
    marginTop: -12,
    "& > h6": {
      fontSize: 14,
    },
  },
  input: {
    padding: "1rem",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    fontWeight: 400,
    width: "100%",
    border: "solid 1px #F3F4F6",
    pointerEvents: "none",
    marginTop: theme.spacing(2),
  },
  headerTitle: {
    marginTop: "1.5rem",
    fontSize: "1.875rem",
    lineHeight: "2.25rem",
    fontWeight: 600,
  },
  image: {
    objectFit: "cover",
    width: "100%",
    height: "100%",
  },

  //others
  noImageIconContainer: {
    color: "white",
    backgroundColor: colors.gray,
    height: 150,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const NoImageIcon = withStyles(ImageIcon, (theme) => ({
  root: {
    color: colors.white,
    width: theme.typography.pxToRem(34),
    height: theme.typography.pxToRem(34),
  },
}));

const PreviewForm: React.FC<Props> = observer(({ form, open, onClose }) => {
  const { classes, cx } = useStyles();

  const _renderElement = (el: FormElement): JSX.Element => {
    switch (el.kind) {
      case FormElementKind.Header:
        return <Header el={el} />;
      case FormElementKind.Description:
        return <Description el={el} />;
      case FormElementKind.LongText:
        return <LongText el={el} />;
      case FormElementKind.ShortText:
        return <ShortText el={el} />;
      case FormElementKind.Dropdown:
        return <Dropdown el={el} />;
      case FormElementKind.Number:
        return <Number el={el} />;
      case FormElementKind.Date:
        return <Date el={el} />;
      case FormElementKind.VerticalSingleSelector:
        return <VerticalSingleSelector el={el} />;
      case FormElementKind.VerticalMultipleSelector:
        return <VerticalMultipleSelector el={el} />;
      case FormElementKind.MediaUrl:
        return <MediaUrl el={el} />;
      case FormElementKind.Checkbox:
        return <Checkbox el={el} />;
      case FormElementKind.Media:
        return <Media el={el} />;
      case FormElementKind.File:
        return <File el={el} />;
      case FormElementKind.Signature:
        return <Signature el={el} />;
      default:
        return <div></div>;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={classes.root}>
        <div className={classes.modal}>
          <Box width="100%" textAlign="end">
            <IconButton
              size="small"
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon color="action" />
            </IconButton>
          </Box>
          <Box>
            {form.sections.map((section) => (
              <div className={classes.sectionContainer} key={section.key}>
                <div className={classes.sectionActions}>
                  <span className={classes.sectionTitle}>
                    <Typography>Section {section.section}</Typography>
                  </span>
                </div>
                <div className={classes.section}>
                  {section.elements.map((element) => (
                    <div
                      key={element.key}
                      className={cx({
                        [classes.border]:
                          element.kind !== FormElementKind.Description &&
                          element.kind !== FormElementKind.Header,
                      })}
                    >
                      {_renderElement(element)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Box>
        </div>
      </div>
    </Modal>
  );
});

// Form components
const Header: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  const editor = useTipTapEditor({ content: el.arguments.label });
  const initialHeaderTitle = "Header title";

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      flexDirection="column"
    >
      {el.singleFileUrl ? (
        <img
          src={el.singleFileUrl}
          alt={humanize(el.kind)}
          className={classes.image}
        />
      ) : (
        <div className={classes.noImageIconContainer}>
          <NoImageIcon />
        </div>
      )}
      {renderDefaultTipTapHTML(editor, initialHeaderTitle)}
    </Box>
  );
});

const Description: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  const editor = useTipTapEditor({ content: el.arguments.description });
  const intialDescription =
    "Lorem ipsum dolor sit amet consectetur adipiscing elit sed deiusmod tempor incididunt consectetur adipiscing elit sed";
  return (
    <Box paddingY={6}>
      {el?.arguments?.label ? (
        <Typography className={classes.label}>{el.arguments.label}</Typography>
      ) : (
        <Typography className={classes.label}>Description Title</Typography>
      )}
      <span className={classes.description}>
        {renderDefaultTipTapHTML(editor, intialDescription)}
      </span>
    </Box>
  );
});

const LongText: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Long text label"}
      </Typography>
      <textarea
        className={classes.input}
        value={el.arguments.defaultText ?? ""}
        required={el.required}
      />
    </Box>
  );
});

const ShortText: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label ?? "Short text label"}
      </Typography>
      <input
        className={classes.input}
        value={el.arguments.defaultText ?? ""}
        required={el.required}
      />
    </Box>
  );
});

const Dropdown: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Dropdown label"}
      </Typography>

      <Select
        variant="outlined"
        style={{ width: "100%", marginTop: 12, pointerEvents: "none" }}
        id="simple-select"
        value={el?.arguments.options?.[0] ?? ""}
      >
        <MenuItem value=""></MenuItem>
        {el.arguments.options?.map((e: string, i: number) => (
          <MenuItem value={e} key={i}>
            {e}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
});

const Number: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Number label"}
      </Typography>
      <input
        className={classes.input}
        value={el.arguments.value ?? ""}
        required={el.required}
      />
    </Box>
  );
});

const Date: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Date label"}
      </Typography>
      <input
        className={classes.input}
        value={el.arguments.dateFormat ?? ""}
        required={el?.required}
      />
    </Box>
  );
});

const VerticalSingleSelector: React.FC<{ el: FormElement }> = observer(
  ({ el }) => {
    const { classes } = useStyles();
    return (
      <Box paddingY={6}>
        <Typography className={classes.label}>
          {el.arguments.label || "Vertical single selector label"}
        </Typography>

        {el.arguments.options && (
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="choices-display"
              name="choices-display"
              value={el.arguments.options?.[0]?.value}
              style={{ pointerEvents: "none" }}
            >
              {el.arguments.options?.map(
                (e: Record<string, any>, i: number) => (
                  <FormControlLabel
                    key={i}
                    value={e.value}
                    control={<Radio />}
                    label={e.value}
                    checked={e.checked}
                  />
                )
              )}
            </RadioGroup>
          </FormControl>
        )}
      </Box>
    );
  }
);

const VerticalMultipleSelector: React.FC<{ el: FormElement }> = observer(
  ({ el }) => {
    const { classes } = useStyles();
    return (
      <Box paddingY={6}>
        <Typography className={classes.label}>
          {el.arguments.label || "Vertical multiple selector label"}
        </Typography>

        {el.arguments.options && (
          <FormGroup row={false} style={{ pointerEvents: "none" }}>
            {el.arguments.options?.map((e: Record<string, any>, i: number) => (
              <FormControlLabel
                key={i}
                control={<MUICheckbox value={e.value} color="primary" />}
                label={e.value}
              />
            ))}
          </FormGroup>
        )}
      </Box>
    );
  }
);

const MediaUrl: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Media URL label"}
      </Typography>
      <input
        className={classes.input}
        required={el.required}
        placeholder="Enter URL address"
      />
    </Box>
  );
});

const Checkbox: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Affirmation label"}
      </Typography>

      <FormGroup row={false} style={{ pointerEvents: "none" }}>
        <FormControlLabel
          control={
            <MUICheckbox
              checked={el.arguments.checked}
              value={el.arguments.affirmation}
              color="primary"
            />
          }
          checked={el.arguments.checked}
          label={el.arguments.affirmation || "I agree with the statement..."}
        />
      </FormGroup>
    </Box>
  );
});

const Media: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Media label"}
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        style={{
          backgroundColor: colors.light,
          border: "1px dashed #a0aec0",
        }}
        padding={8}
        marginTop={2}
      >
        <StyledImageIcon />
        <Typography variant="body2" color="textSecondary">
          Drop your photos and videos here or upload from your device
        </Typography>
      </Box>
    </Box>
  );
});

const File: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "File label"}
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        style={{
          backgroundColor: colors.light,
          border: "1px dashed #a0aec0",
        }}
        padding={8}
        marginTop={2}
      >
        <StyledFileIcon />
        <Typography variant="body2" color="textSecondary">
          Drop your files here or upload from your device
        </Typography>
      </Box>
    </Box>
  );
});

const Signature: React.FC<{ el: FormElement }> = observer(({ el }) => {
  const { classes } = useStyles();
  return (
    <Box paddingY={6}>
      <Typography className={classes.label}>
        {el.arguments.label || "Signature label"}
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        style={{
          backgroundColor: colors.light,
          border: "1px dashed #a0aec0",
        }}
        padding={8}
        marginTop={2}
      >
        <>
          <Typography variant="body2" color="textSecondary">
            Draw your signature here
          </Typography>
        </>
      </Box>
    </Box>
  );
});

export default PreviewForm;
